import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../generated/api-client";
import { UploadDropzone } from "../../components/cost-estimation/UploadDropzone";
import {
  catalogImportApi,
  CatalogImportJobStatus,
  CatalogImportPreviewResult,
  CatalogPreviewItem,
  CatalogSourceCheckResult,
  CatalogSourceEntry,
} from "../../services/catalogImportApi";
import { CATEGORIES, DISCIPLINES } from "../../services/configApi";
import { formatDateTimeRome, getCurrentYearRome } from "../../utils/dateFormat";
import { normalizeUdm } from "../../utils/matchSelection";

// Steps: 0=Upload  1=Preview  2=Importing  3=Done
type WizardStep = 0 | 1 | 2 | 3;

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  border: "1px solid #6366f1",
  borderRadius: 3,
  padding: "1px 3px",
  fontSize: 11,
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
};

const POLL_INTERVAL_MS = 2_000;

export const CatalogImportPage: React.FC = () => {
  const { user } = useAuth();
  const isAllowed =
    user?.role === UserRole.Admin || user?.role === UserRole.Mantainer;

  const [activeStep, setActiveStep] = useState<WizardStep>(0);
  const [file, setFile] = useState<File | null>(null);
  // Bytes letti subito al momento della selezione — evita ERR_UPLOAD_FILE_CHANGED
  // che Chrome lancia quando il riferimento File è tenuto in stato per minuti.
  const fileBytesRef = useRef<{ bytes: ArrayBuffer; name: string; type: string } | null>(null);
  const [categoria, setCategoria] = useState("");
  const [disciplina, setDisciplina] = useState("");
  const [anno, setAnno] = useState<string>(String(getCurrentYearRome()));

  const [previewing, setPreviewing] = useState(false);
  const [checkingSource, setCheckingSource] = useState(false);
  const [preview, setPreview] = useState<CatalogImportPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<CatalogSourceCheckResult | null>(null);

  // Preview inline-editing state
  const [editedItems, setEditedItems] = useState<CatalogPreviewItem[]>([]);
  const [editingCell, setEditingCell] = useState<{ rowIdx: number; field: string } | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  // Indici delle righe eliminate dall'utente in anteprima
  const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set());

  // Background import state
  const [starting, setStarting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<CatalogImportJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // History table state
  const [sources, setSources] = useState<CatalogSourceEntry[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesSearch, setSourcesSearch] = useState("");
  const [sourcesAnno, setSourcesAnno] = useState("");
  const [sourcesCategoria, setSourcesCategoria] = useState("");
  const [sourcesDisciplina, setSourcesDisciplina] = useState("");

  const navigate = useNavigate();
  const [sourceToDelete, setSourceToDelete] = useState<CatalogSourceEntry | null>(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deletingSource, setDeletingSource] = useState(false);

  const loadSources = async () => {
    setSourcesLoading(true);
    try {
      const data = await catalogImportApi.listSources();
      setSources(data);
    } catch {
      // non-fatal
    } finally {
      setSourcesLoading(false);
    }
  };

  // All hooks before early returns
  useEffect(() => {
    loadSources();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  /**
   * Costruisce un File fresco dai byte già letti — Chrome invalida il riferimento
   * originale dopo alcuni minuti (ERR_UPLOAD_FILE_CHANGED), ma i byte rimangono
   * validi finché sono in memoria.
   */
  const getFreshFile = (): File | null => {
    if (fileBytesRef.current) {
      return new File(
        [fileBytesRef.current.bytes],
        fileBytesRef.current.name,
        { type: fileBytesRef.current.type }
      );
    }
    return file;
  };

  useEffect(() => {
    if (preview) setEditedItems([...preview.preview_items]);
  }, [preview]);

  if (!isAllowed) {
    return <Navigate to="/cost-estimation" replace />;
  }

  // ── Polling ──────────────────────────────────────────────────────────────────

  const startPolling = (id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await catalogImportApi.getJob(id);
        setJobStatus(status);
        if (status.status === "done") {
          stopPolling();
          setActiveStep(3);
          toast.success(
            `${status.created + status.updated} elementi importati nel catalogo`
          );
          loadSources();
        } else if (status.status === "failed") {
          stopPolling();
          toast.error(status.error ?? "Importazione fallita");
        }
      } catch (e: any) {
        if (e?.response?.status === 404) {
          stopPolling();
          toast.error(
            "Job di importazione non trovato — il server potrebbe essersi riavviato. Verifica il catalogo e riprova se necessario."
          );
        }
        // altri errori: retry al prossimo tick
      }
    }, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const runPreview = async () => {
    const freshFile = getFreshFile();
    if (!freshFile) return;
    setPreviewError(null);
    setPreviewing(true);
    try {
      const result = await catalogImportApi.preview(freshFile);
      setPreview(result);
      if (result.prefill?.categoria && !categoria) setCategoria(result.prefill.categoria);
      if (result.prefill?.disciplina && !disciplina) setDisciplina(result.prefill.disciplina);
      setActiveStep(1);
    } catch (e: any) {
      setPreviewError(e?.response?.data?.detail ?? "Errore nell'analisi del file.");
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreview = async () => {
    if (!file || !categoria || !disciplina) {
      toast.error("Seleziona file, categoria e disciplina");
      return;
    }
    setCheckingSource(true);
    try {
      const check = await catalogImportApi.checkSource(file.name);
      if (check.exists) {
        setDuplicateSource(check);
        setDuplicateDialogOpen(true);
        return;
      }
      await runPreview();
    } catch (e: any) {
      setPreviewError(e?.response?.data?.detail ?? "Errore nel controllo della fonte.");
    } finally {
      setCheckingSource(false);
    }
  };

  const handleConfirmDuplicatePreview = async () => {
    setDuplicateDialogOpen(false);
    await runPreview();
  };

  const handleStartImport = async () => {
    if (!preview) return;
    const freshFile = getFreshFile();
    if (!freshFile) return;
    setStarting(true);
    const excludedPositions = editedItems
      .filter((_, idx) => deletedIndices.has(idx) && !editedItems[idx].is_section)
      .map((it) => it.numero)
      .filter(Boolean);

    try {
      const { job_id } = await catalogImportApi.start(freshFile, {
        categoria,
        disciplina,
        anno: anno ? parseInt(anno) : undefined,
        excludedPositions: excludedPositions.length > 0 ? excludedPositions : undefined,
      });
      setJobId(job_id);
      setJobStatus(null);
      setActiveStep(2);
      startPolling(job_id);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) {
        toast.error("Sessione scaduta — effettua di nuovo il login e riprova.");
      } else {
        toast.error(detail ?? "Errore nell'avvio dell'importazione");
      }
    } finally {
      setStarting(false);
    }
  };

  // ── Inline cell editing ───────────────────────────────────────────────────────

  const startEdit = (rowIdx: number, field: string, currentValue: string) => {
    setEditingCell({ rowIdx, field });
    setEditingValue(currentValue);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { rowIdx, field } = editingCell;
    const NUMERIC = new Set(["prezzo", "costo_mat_unitario", "tempo_posa_ore", "costo_mano_unitario", "qta"]);
    setEditedItems((prev) => {
      const next = [...prev];
      const item = { ...next[rowIdx] };
      if (NUMERIC.has(field)) {
        const trimmed = editingValue.trim().replace(",", ".");
        const parsed = parseFloat(trimmed);
        (item as any)[field] = trimmed === "" || isNaN(parsed) ? null : parsed;
      } else if (field === "udm") {
        (item as any)[field] = normalizeUdm(editingValue) || null;
      } else {
        (item as any)[field] = editingValue.trim() || null;
      }
      next[rowIdx] = item;
      return next;
    });
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  const handleEditKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") cancelEdit();
  };

  const isEditingCell = (rowIdx: number, field: string) =>
    editingCell?.rowIdx === rowIdx && editingCell?.field === field;

  const handleDeleteRow = (idx: number) => {
    setDeletedIndices((prev) => {
      const next = new Set(Array.from(prev));
      next.add(idx);
      return next;
    });
    setEditingCell(null);
  };

  const handleReset = () => {
    stopPolling();
    setActiveStep(0);
    setFile(null);
    fileBytesRef.current = null;
    setPreview(null);
    setJobId(null);
    setJobStatus(null);
    setPreviewError(null);
    setDuplicateDialogOpen(false);
    setDuplicateSource(null);
    setDeletedIndices(new Set());
  };

  const openDeleteSource = (s: CatalogSourceEntry) => {
    setDeleteAcknowledged(false);
    setSourceToDelete(s);
  };

  const closeDeleteSource = () => {
    if (deletingSource) return;
    setSourceToDelete(null);
  };

  const handleConfirmDeleteSource = async () => {
    if (!sourceToDelete) return;
    setDeletingSource(true);
    try {
      const res = await catalogImportApi.deleteSource(sourceToDelete);
      toast.success(
        `${res.deleted.toLocaleString("it-IT")} ${res.deleted === 1 ? "elemento eliminato" : "elementi eliminati"} dal catalogo`
      );
      setSourceToDelete(null);
      loadSources();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? "Errore durante l'eliminazione del file");
    } finally {
      setDeletingSource(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────────

  const effectiveCategoria = preview?.prefill?.categoria || categoria;
  const effectiveDisciplina = preview?.prefill?.disciplina || disciplina;

  const filteredSources = sources.filter((s) => {
    if (sourcesSearch && !s.source_file.toLowerCase().includes(sourcesSearch.toLowerCase())) return false;
    if (sourcesAnno && String(s.anno ?? "") !== sourcesAnno) return false;
    if (sourcesCategoria && s.categoria !== sourcesCategoria) return false;
    if (sourcesDisciplina && s.disciplina !== sourcesDisciplina) return false;
    return true;
  });

  const sourceYears = Array.from(new Set(sources.map((s) => s.anno).filter(Boolean))).sort((a, b) => (b as number) - (a as number));

  const progressPct =
    jobStatus && jobStatus.total > 0
      ? Math.round((jobStatus.processed / jobStatus.total) * 100)
      : 0;

  const isImporting =
    activeStep === 2 &&
    (!jobStatus || jobStatus.status === "pending" || jobStatus.status === "processing");

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Importa analisi costi nel catalogo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Carica un file Excel "Tabella Analisi Costi" per importare o aggiornare elementi
        nel catalogo. Solo admin e maintainer possono eseguire questa operazione.
      </Typography>

      <Stepper activeStep={Math.min(activeStep, 2)} sx={{ mb: 4 }}>
        <Step><StepLabel>Carica file</StepLabel></Step>
        <Step><StepLabel>Anteprima</StepLabel></Step>
        <Step><StepLabel>Completato</StepLabel></Step>
      </Stepper>

      {/* ── Step 0: Upload + metadata ──────────────────────────────────────── */}
      {activeStep === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {previewError && (
            <Alert severity="error" onClose={() => setPreviewError(null)}>
              <AlertTitle>Errore analisi</AlertTitle>
              {previewError}
            </Alert>
          )}

          <UploadDropzone
            file={file}
            onChange={(f) => {
              setFile(f);
              setPreviewError(null);
              // Leggi i byte subito — evita ERR_UPLOAD_FILE_CHANGED dopo inattività
              if (f) {
                f.arrayBuffer().then((buf) => {
                  fileBytesRef.current = { bytes: buf, name: f.name, type: f.type };
                });
              } else {
                fileBytesRef.current = null;
              }
            }}
            disabled={previewing || checkingSource}
            placeholder="Trascina il file Excel di analisi costi (.xlsx, .xls)"
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ flex: 2 }} required>
              <InputLabel>Categoria *</InputLabel>
              <Select
                value={categoria}
                label="Categoria *"
                onChange={(e) => setCategoria(e.target.value)}
                disabled={previewing || checkingSource}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }} required>
              <InputLabel>Disciplina *</InputLabel>
              <Select
                value={disciplina}
                label="Disciplina *"
                onChange={(e) => setDisciplina(e.target.value)}
                disabled={previewing || checkingSource}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Anno"
              value={anno}
              onChange={(e) => setAnno(e.target.value)}
              size="small"
              type="number"
              sx={{ flex: 1 }}
              disabled={previewing || checkingSource}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={previewing || checkingSource || !file || !categoria || !disciplina}
            sx={{ alignSelf: "flex-start", borderRadius: "10px" }}
            startIcon={
              previewing || checkingSource
                ? <CircularProgress size={18} />
                : <UploadFileIcon />
            }
          >
            {checkingSource
              ? "Controllo fonte…"
              : previewing
                ? "Analisi in corso…"
                : "Analizza file"}
          </Button>
        </Box>
      )}

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => !previewing && setDuplicateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: "warning.main" }} />
          Fonte già presente nel catalogo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Un file con lo stesso nome risulta già importato tra le fonti del catalogo.
            Procedendo verranno aggiunti nuovi elementi (non verranno aggiornati quelli esistenti).
          </Typography>
          {duplicateSource && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: "10px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography variant="body2">
                <strong>File:</strong> {duplicateSource.source_file}
              </Typography>
              <Typography variant="body2">
                <strong>Elementi in catalogo:</strong> {duplicateSource.item_count.toLocaleString("it-IT")}
              </Typography>
              {duplicateSource.last_imported_at && (
                <Typography variant="body2">
                  <strong>Ultimo import:</strong> {formatDateTimeRome(duplicateSource.last_imported_at) ?? "—"}
                </Typography>
              )}
              {(duplicateSource.categoria || duplicateSource.disciplina || duplicateSource.anno) && (
                <Typography variant="body2" color="text.secondary">
                  {[
                    duplicateSource.categoria,
                    duplicateSource.disciplina,
                    duplicateSource.anno ? String(duplicateSource.anno) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} disabled={previewing}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDuplicatePreview}
            disabled={previewing}
            startIcon={previewing ? <CircularProgress size={18} /> : undefined}
          >
            {previewing ? "Analisi in corso…" : "Procedi comunque"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Step 1: Preview KPIs + full items table ───────────────────────── */}
      {activeStep === 1 && preview && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* KPI banner */}
          <Paper
            elevation={0}
            sx={{
              border: "1.5px solid",
              borderColor: "success.light",
              bgcolor: "#F1F8F4",
              borderRadius: "14px",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "success.dark" }}>
                Analisi completata
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{preview.total_items}</Typography>
                <Typography variant="caption" color="text.secondary">elementi estratti</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{preview.total_sections}</Typography>
                <Typography variant="caption" color="text.secondary">sezioni</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{preview.items_with_costs}</Typography>
                <Typography variant="caption" color="text.secondary">righe con costi</Typography>
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Chip
                label={`Categoria: ${effectiveCategoria}`}
                size="small"
                sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 600 }}
              />
              <Chip
                label={`Disciplina: ${effectiveDisciplina}`}
                size="small"
                sx={{ bgcolor: "#F3E5F5", color: "#7B1FA2", fontWeight: 600 }}
              />
              {anno && <Chip label={`Anno: ${anno}`} size="small" variant="outlined" />}
              {preview.items_missing_costs > 0 && (
                <Chip
                  label={`${preview.items_missing_costs} senza costi`}
                  size="small"
                  sx={{ bgcolor: "#FFF3E0", color: "#E65100" }}
                />
              )}
            </Box>
          </Paper>

          {preview.warnings.length > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              <AlertTitle>Avvisi ({preview.warnings.length})</AlertTitle>
              <List dense sx={{ mt: 0.5, py: 0 }}>
                {preview.warnings.slice(0, 5).map((w, i) => (
                  <ListItem key={i} sx={{ py: 0.25 }}>
                    <ListItemText primary={w} primaryTypographyProps={{ variant: "caption" }} />
                  </ListItem>
                ))}
                {preview.warnings.length > 5 && (
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`… e altri ${preview.warnings.length - 5} avvisi`}
                      primaryTypographyProps={{ variant: "caption", color: "text.disabled" }}
                    />
                  </ListItem>
                )}
              </List>
            </Alert>
          )}

          {/* Full items table */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Tutti gli elementi estratti ({editedItems.filter((r, i) => !r.is_section && !deletedIndices.has(i)).length})
              {editedItems.some((r) => r.is_section) && (
                <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 1 }}>
                  · {editedItems.filter((r) => r.is_section).length} sezioni
                </Box>
              )}
              {deletedIndices.size > 0 && (
                <Box component="span" sx={{ fontWeight: 400, color: "error.main", ml: 1, fontSize: 10 }}>
                  · {deletedIndices.size} rimoss{deletedIndices.size === 1 ? "a" : "e"}
                </Box>
              )}
              <Box component="span" sx={{ fontWeight: 400, color: "text.disabled", ml: 1, fontSize: 10 }}>
                — doppio click su una cella per modificarla
              </Box>
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {/* Sticky header */}
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                  tableLayout: "fixed",
                  "& th": {
                    bgcolor: "action.hover",
                    fontWeight: 700,
                    textAlign: "left",
                    px: 0.75,
                    py: 0.75,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "5%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Codice</th>
                    <th>Descr. breve</th>
                    <th>Descr. estesa</th>
                    <th>UM</th>
                    <th style={{ textAlign: "right" }}>€ unit.</th>
                    <th style={{ textAlign: "right" }}>€ mat.</th>
                    <th style={{ textAlign: "right" }}>Ore man.</th>
                    <th style={{ textAlign: "right" }}>€ mano</th>
                    <th />
                  </tr>
                </thead>
              </Box>

              {/* Scrollable body */}
              <Box
                sx={{
                  maxHeight: 520,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 11,
                    tableLayout: "fixed",
                    "& td": {
                      px: 0.75,
                      py: 0.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      verticalAlign: "top",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "& tr:last-child td": { borderBottom: "none" },
                    "& tr:hover td": { bgcolor: "action.hover" },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "4%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "19%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "4%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "5%" }} />
                  </colgroup>
                  <tbody>
                    {editedItems.map((it: CatalogPreviewItem, i: number) => {
                      if (deletedIndices.has(i)) return null;
                      return it.is_section ? (
                        /* Section / chapter header row — shown for debug, never imported */
                        <tr key={i} style={{ backgroundColor: "rgba(99,102,241,0.07)" }}>
                          <td style={{ color: "#6366f1", whiteSpace: "nowrap", fontWeight: 700 }}>{it.numero}</td>
                          <td colSpan={7} style={{ fontWeight: 700, color: "#4f46e5", fontSize: 11 }}>
                            <span
                              style={{
                                display: "inline-block",
                                background: "rgba(99,102,241,0.15)",
                                color: "#4338ca",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                marginRight: 6,
                                verticalAlign: "middle",
                              }}
                            >
                              sezione
                            </span>
                            <Tooltip
                              title={it.descrizione}
                              placement="top-start"
                              disableHoverListener={it.descrizione.length < 80}
                            >
                              <span style={{ verticalAlign: "middle", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {it.descrizione}
                              </span>
                            </Tooltip>
                          </td>
                          <td />
                        </tr>
                      ) : (
                        <tr key={i}>
                          {/* numero — structural, not editable */}
                          <td style={{ color: "#888", whiteSpace: "nowrap" }}>{it.numero}</td>

                          {/* codice */}
                          <td
                            style={{ fontFamily: "monospace", fontSize: 10, wordBreak: "break-all", cursor: "text" }}
                            onDoubleClick={() => startEdit(i, "codice", it.codice ?? "")}
                          >
                            {isEditingCell(i, "codice") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey}
                                style={{ ...INPUT_STYLE, fontFamily: "monospace" }} />
                            ) : (
                              it.codice ?? <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>

                          {/* descrizione_breve */}
                          <td style={{ cursor: "text" }} onDoubleClick={() => startEdit(i, "descrizione_breve", it.descrizione_breve ?? "")}>
                            {isEditingCell(i, "descrizione_breve") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey} style={INPUT_STYLE} />
                            ) : (
                              <Tooltip title={it.descrizione_breve ?? ""} placement="top-start"
                                disableHoverListener={!it.descrizione_breve || it.descrizione_breve.length < 40}>
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {it.descrizione_breve ?? <span style={{ color: "#bbb" }}>—</span>}
                                </span>
                              </Tooltip>
                            )}
                          </td>

                          {/* descrizione estesa */}
                          <td style={{ cursor: "text" }} onDoubleClick={() => startEdit(i, "descrizione", it.descrizione)}>
                            {isEditingCell(i, "descrizione") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey} style={INPUT_STYLE} />
                            ) : (
                              <Tooltip title={it.descrizione} placement="top-start"
                                disableHoverListener={it.descrizione.length < 60}>
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {it.descrizione || <span style={{ color: "#bbb" }}>—</span>}
                                </span>
                              </Tooltip>
                            )}
                          </td>

                          {/* udm */}
                          <td style={{ color: "#555", cursor: "text" }} onDoubleClick={() => startEdit(i, "udm", it.udm ?? "")}>
                            {isEditingCell(i, "udm") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey} style={INPUT_STYLE} />
                            ) : (
                              it.udm ?? "—"
                            )}
                          </td>

                          {/* prezzo */}
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "text" }}
                            onDoubleClick={() => startEdit(i, "prezzo", it.prezzo != null ? String(it.prezzo) : "")}>
                            {isEditingCell(i, "prezzo") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey}
                                style={{ ...INPUT_STYLE, textAlign: "right" }} />
                            ) : (
                              it.prezzo != null ? it.prezzo.toFixed(2) : <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>

                          {/* costo_mat_unitario */}
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "text" }}
                            onDoubleClick={() => startEdit(i, "costo_mat_unitario", it.costo_mat_unitario != null ? String(it.costo_mat_unitario) : "")}>
                            {isEditingCell(i, "costo_mat_unitario") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey}
                                style={{ ...INPUT_STYLE, textAlign: "right" }} />
                            ) : (
                              it.costo_mat_unitario != null
                                ? it.costo_mat_unitario.toFixed(2)
                                : <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>

                          {/* tempo_posa_ore */}
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "text" }}
                            onDoubleClick={() => startEdit(i, "tempo_posa_ore", it.tempo_posa_ore != null ? String(it.tempo_posa_ore) : "")}>
                            {isEditingCell(i, "tempo_posa_ore") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey}
                                style={{ ...INPUT_STYLE, textAlign: "right" }} />
                            ) : (
                              it.tempo_posa_ore != null
                                ? it.tempo_posa_ore.toFixed(3).replace(/\.?0+$/, "")
                                : <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>

                          {/* costo_mano_unitario */}
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "text" }}
                            onDoubleClick={() => startEdit(i, "costo_mano_unitario", it.costo_mano_unitario != null ? String(it.costo_mano_unitario) : "")}>
                            {isEditingCell(i, "costo_mano_unitario") ? (
                              <input autoFocus value={editingValue} onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit} onKeyDown={handleEditKey}
                                style={{ ...INPUT_STYLE, textAlign: "right" }} />
                            ) : (
                              it.costo_mano_unitario != null
                                ? it.costo_mano_unitario.toFixed(2)
                                : <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>

                          {/* delete row */}
                          <td style={{ textAlign: "center", padding: "2px 0" }}>
                            <Tooltip title="Rimuovi dalla lista" placement="left">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRow(i)}
                                sx={{
                                  p: "2px",
                                  color: "text.disabled",
                                  "&:hover": { color: "error.main", bgcolor: "error.50" },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={handleReset} disabled={starting}>
              ← Ricomincia
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={starting}
              startIcon={starting ? <CircularProgress size={18} /> : undefined}
            >
              {starting
                ? "Avvio importazione…"
                : `Importa ${preview.total_items - deletedIndices.size} elementi →`}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Step 2: Importing (progress) ──────────────────────────────────── */}
      {activeStep === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper
            elevation={0}
            sx={{
              border: "1.5px solid",
              borderColor: "primary.light",
              bgcolor: "#F0F4FF",
              borderRadius: "14px",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.dark" }}>
                Importazione in corso…
              </Typography>
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {jobStatus
                    ? `${jobStatus.processed} / ${jobStatus.total} elementi elaborati`
                    : "Avvio…"}
                </Typography>
                {jobStatus && jobStatus.total > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {progressPct}%
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant={
                  jobStatus && jobStatus.total > 0 ? "determinate" : "indeterminate"
                }
                value={jobStatus && jobStatus.total > 0 ? progressPct : undefined}
                sx={{ borderRadius: 4, height: 8 }}
              />
            </Box>

            {jobStatus && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  label={`${jobStatus.created} creati`}
                  size="small"
                  sx={{ bgcolor: "#E8F5E9", color: "#2E7D32" }}
                />
                <Chip
                  label={`${jobStatus.updated} aggiornati`}
                  size="small"
                  sx={{ bgcolor: "#E3F2FD", color: "#1565C0" }}
                />
                {jobStatus.skipped > 0 && (
                  <Chip
                    label={`${jobStatus.skipped} saltati`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {jobStatus.elapsed_seconds != null && (
                  <Chip
                    label={`${jobStatus.elapsed_seconds}s`}
                    size="small"
                    variant="outlined"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              Puoi chiudere questa pagina — l'importazione continua in background.
            </Typography>
          </Paper>

          {jobStatus?.status === "failed" && (
            <Alert severity="error">
              <AlertTitle>Importazione fallita</AlertTitle>
              {jobStatus.error}
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={handleReset}>
                  Riprova
                </Button>
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {/* ── Step 3: Done ──────────────────────────────────────────────────── */}
      {activeStep === 3 && jobStatus && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: "1.5px solid",
              borderColor: "success.light",
              bgcolor: "#F1F8F4",
              borderRadius: "14px",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: "2rem" }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "success.dark" }}
                >
                  Importazione completata
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {jobStatus.created} creati · {jobStatus.updated} aggiornati
                  {jobStatus.skipped > 0 && ` · ${jobStatus.skipped} saltati`}
                  {jobStatus.elapsed_seconds != null &&
                    ` · ${jobStatus.elapsed_seconds}s`}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {jobStatus.warnings.length > 0 && (
            <Alert severity="warning">
              {jobStatus.warnings.length} avvisi durante l'importazione.
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleReset}
            sx={{ alignSelf: "flex-start" }}
          >
            Importa un altro file
          </Button>
        </Box>
      )}

      {/* ── Storico file importati ─────────────────────────────────────────── */}
      <Divider sx={{ mt: 5, mb: 3 }} />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <FolderOpenIcon sx={{ color: "text.secondary", fontSize: "1.3rem" }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            File importati in catalogo
          </Typography>
          {sourcesLoading && <CircularProgress size={14} />}
          {!sourcesLoading && (
            <Chip
              label={`${filteredSources.length}${filteredSources.length !== sources.length ? ` / ${sources.length}` : ""}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Cerca per nome file…"
            value={sourcesSearch}
            onChange={(e) => setSourcesSearch(e.target.value)}
            sx={{ flex: 2, minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={sourcesAnno}
              label="Anno"
              onChange={(e) => setSourcesAnno(e.target.value)}
            >
              <MenuItem value=""><em>Tutti</em></MenuItem>
              {sourceYears.map((y) => (
                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={sourcesCategoria}
              label="Categoria"
              onChange={(e) => setSourcesCategoria(e.target.value)}
            >
              <MenuItem value=""><em>Tutte</em></MenuItem>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Disciplina</InputLabel>
            <Select
              value={sourcesDisciplina}
              label="Disciplina"
              onChange={(e) => setSourcesDisciplina(e.target.value)}
            >
              <MenuItem value=""><em>Tutte</em></MenuItem>
              {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
          {(sourcesSearch || sourcesAnno || sourcesCategoria || sourcesDisciplina) && (
            <Button
              size="small"
              variant="text"
              onClick={() => { setSourcesSearch(""); setSourcesAnno(""); setSourcesCategoria(""); setSourcesDisciplina(""); }}
              sx={{ color: "text.secondary" }}
            >
              Azzera filtri
            </Button>
          )}
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: "10px", maxHeight: 420, overflow: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5" }}>File</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 100 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 80 }}>Disciplina</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 60 }}>Anno</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 80 }} align="right">Voci</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 150 }}>Importato il</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#F5F5F5", width: 84 }} align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sourcesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : filteredSources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary", fontSize: "0.82rem" }}>
                    {sources.length === 0
                      ? "Nessun file ancora importato"
                      : "Nessun risultato per i filtri selezionati"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSources.map((s, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <Tooltip title={s.source_file} placement="top-start" disableHoverListener={s.source_file.length < 50}>
                        <span>{s.source_file}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {s.categoria
                        ? <Chip label={s.categoria} size="small" sx={{ fontSize: "0.68rem", height: 20, bgcolor: "#E3F2FD", color: "#1565C0" }} />
                        : <span style={{ color: "#bbb" }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {s.disciplina
                        ? <Chip label={s.disciplina} size="small" sx={{ fontSize: "0.68rem", height: 20, bgcolor: s.disciplina === "ELE" ? "#E3F2FD" : "#FFF3E0", color: s.disciplina === "ELE" ? "#1565C0" : "#E65100", fontWeight: 700 }} />
                        : <span style={{ color: "#bbb" }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{s.anno ?? <span style={{ color: "#bbb" }}>—</span>}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", fontVariantNumeric: "tabular-nums" }} align="right">{s.item_count.toLocaleString("it-IT")}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                      {s.last_imported_at ? formatDateTimeRome(s.last_imported_at) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.25, whiteSpace: "nowrap" }}>
                      <Tooltip title="Vedi gli elementi nel catalogo">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cost-estimation/search?source_file=${encodeURIComponent(s.source_file)}`)}
                          sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina dal catalogo tutti gli elementi di questo file">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteSource(s)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main", bgcolor: "rgba(211,47,47,0.06)" } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "1.05rem" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={!!sourceToDelete}
        onClose={closeDeleteSource}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteOutlineIcon sx={{ color: "error.main" }} />
          Elimina file dal catalogo
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Questa operazione è <strong>irreversibile</strong>: gli elementi eliminati non potranno
            essere recuperati e non verranno più proposti come match.
          </Alert>
          {sourceToDelete && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: "10px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                <strong>File:</strong> {sourceToDelete.source_file}
              </Typography>
              <Typography variant="body2">
                <strong>Elementi da eliminare:</strong> {sourceToDelete.item_count.toLocaleString("it-IT")}
              </Typography>
              {(sourceToDelete.categoria || sourceToDelete.disciplina || sourceToDelete.anno) && (
                <Typography variant="body2" color="text.secondary">
                  {[sourceToDelete.categoria, sourceToDelete.disciplina, sourceToDelete.anno ? String(sourceToDelete.anno) : null]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            I preventivi già compilati mantengono i valori dei match assegnati.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                color="error"
                checked={deleteAcknowledged}
                onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                disabled={deletingSource}
              />
            }
            label={<Typography variant="body2">Ho capito, elimina definitivamente questi elementi</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteSource} disabled={deletingSource} color="inherit">
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteSource}
            disabled={!deleteAcknowledged || deletingSource}
            startIcon={deletingSource ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {deletingSource
              ? "Eliminazione…"
              : sourceToDelete?.item_count === 1
                ? "Elimina 1 elemento"
                : `Elimina ${sourceToDelete?.item_count.toLocaleString("it-IT") ?? ""} elementi`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
